import * as TemplateTypes from 'types/template.types';
import Phaser from 'phaser';
import Transitions from 'src/fx/transition/transitions';
import { useModule } from 'src/modules/modules';

interface TransitionInstances {
    scene: {
        enter: null | TemplateTypes.SceneTransition,
        exit: null | TemplateTypes.SceneTransition,
    },
    audio: {
        enter: null | TemplateTypes.AudioTransition,
        exit: null | TemplateTypes.AudioTransition,
    },
    menu: {
        enter: null | TemplateTypes.MenuTransition,
        exit: null | TemplateTypes.MenuTransition,
    }
}

const getTransitions = (scene: BaseScene, config: TemplateTypes.BaseSceneConfig, track?: Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound) => {
    let transitions : TransitionInstances = {
        scene: {
            enter: null,
            exit: null,
        },
        audio: {
            enter: null,
            exit: null
        },
        menu: {
            enter: null,
            exit: null
        }
    }

    if (!config.transition) return transitions;

    //Enter scene
    let enterSceneConfig = config.transition?.scene?.enter;
    if (enterSceneConfig){
        if (typeof enterSceneConfig === 'string'){
            transitions.scene.enter = new Transitions.Scene[enterSceneConfig](scene);
        } else if (enterSceneConfig.name) {
            transitions.scene.enter = new Transitions.Scene[enterSceneConfig?.name](scene, enterSceneConfig);
        }
    }

    //Exit scene
    let exitSceneConfig = config.transition?.scene?.exit;
    if (exitSceneConfig){
        if (typeof exitSceneConfig === 'string'){
            transitions.scene.exit = new Transitions.Scene[exitSceneConfig](scene);
        } else if (exitSceneConfig.name) {
            transitions.scene.exit = new Transitions.Scene[exitSceneConfig?.name](scene, exitSceneConfig);
        }
    }

    if (track){
        //Enter audio
        let enterAudioConfig = config.transition?.audio?.enter;
        if (enterAudioConfig){
            if (typeof enterAudioConfig === 'string'){
                transitions.audio.enter = new Transitions.Audio[enterAudioConfig](scene, track);
            } else if (enterAudioConfig.name) {
                transitions.audio.enter = new Transitions.Audio[enterAudioConfig?.name](scene, track, enterAudioConfig);
            }
        }

        //Exit audio
        let exitAudioConfig = config.transition?.audio?.exit;
        if (exitAudioConfig){
            if (typeof exitAudioConfig === 'string'){
                transitions.audio.exit = new Transitions.Audio[exitAudioConfig](scene, track);
            } else if (exitAudioConfig.name) {
                transitions.audio.exit = new Transitions.Audio[exitAudioConfig?.name](scene, track, exitAudioConfig);
            }
        }
    }

    //Enter menu
    let enterMenuConfig = config.transition?.menu?.enter;
    if (enterMenuConfig){
        if (typeof enterMenuConfig === 'string'){
            transitions.menu.enter =  new Transitions.Menu[enterMenuConfig]({}); 
        } else if (enterMenuConfig.name) {
            transitions.menu.enter = new Transitions.Menu[enterMenuConfig?.name](enterMenuConfig);
        }
    }

    //Exit menu
    let exitMenuConfig = config.transition?.menu?.exit;
    if (exitMenuConfig){
        if (typeof exitMenuConfig === 'string'){
            transitions.menu.exit = new Transitions.Menu[exitMenuConfig]({});
        } else if (exitMenuConfig.name) {
            transitions.menu.exit = new Transitions.Menu[exitMenuConfig?.name](exitMenuConfig);
        }
    }

    return transitions;
}

interface Updateable {
    update: (time: number, delta: number) => any
}

class BaseScene extends Phaser.Scene {

    private config: TemplateTypes.BaseSceneConfig;
    private track?: Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound;
    private transitions?: TransitionInstances;
    private updateables: Set<Updateable> = new Set();
    public readonly sceneKey: TemplateTypes.Scene;

    constructor(config: TemplateTypes.BaseSceneConfig){
        super(config);
        this.config = config;
        this.sceneKey = config.key;
    }

    init(){
        const { global, audio } = useModule();

        /**
         * Update the active scene
         */
        global.updateSceneState({
            scene: this,
            exiting: true,
            transitionClass: ''
        })
        /**
         * Init audio
         */
        if (this.config.trackKey){
            this.track = audio.add(this.config.trackKey);
            this.track.loop = true;
        }
        /**
         * Generate our transition instances
         */
        this.transitions = getTransitions(this, this.config, this.track);
        this.events.on('shutdown', this.destroy, this);
    }

    async enter(){
        const { global } = useModule();

        /**
         * Do audio transition if exists, otherwise just play the audio track 
         * if one was provided. 
         */
        if (this.track){
            if (this.transitions?.audio.enter){
                this.transitions?.audio.enter.enter();
            } else {
                this.track.play();
            }
        }
        /**
         * Notify store that we are entering so that Vue can begin its enter transition if applicable.
         */
        global.updateSceneState({
            scene: this,
            exiting: false,
            transitionClass: this.transitions?.menu.enter?.getClassName() ?? ''
        })

        await this.transitions?.scene.enter?.enter();

        /**
         * Notify again just to clear transition class. This will prevent unwanted transitions
         */
        global.updateSceneState({
            scene: this,
            exiting: false,
            transitionClass: ''
        });
    }

    async exit(nextSceneKey: string){
        const { global } = useModule();

        /**
         * Do audio transition if exists, otherwise just play the audio track 
         * if one was provided. 
         */
        if (this.track){
            //TODO: Populate track here
            if (this.transitions?.audio.exit){
                this.transitions?.audio.exit.exit();
            } else {
                this.track.stop();
                this.track.destroy();
            }
        }
        /**
         * Notify store that we are exiting so that Vue can begin its exit transition if applicable.
         */
        global.updateSceneState({
            scene: this,
            exiting: true,
            transitionClass: this.transitions?.menu.exit?.getClassName() ?? ''
        });

        /**
         * Wait for our exit to complete and then actually switch scenes
         */
        await this.transitions?.scene.exit?.exit();
        this.scene.start(nextSceneKey);
    }

    registerUpdate(item: Updateable){
        this.updateables.add(item);
    }

    unregisterUpdate(item: Updateable){
        this.updateables.delete(item);
    }

    update(time: number, delta: number): void {
        this.updateables.forEach(u => u.update(time, delta));
    }

    destroy(){
        this.transitions?.scene.enter?.destroy();
        this.transitions?.scene.exit?.destroy();
        this.transitions?.audio.enter?.destroy();
        this.transitions?.audio.exit?.destroy();
        delete this.transitions;
    }
}

export default BaseScene;